import { useState, useEffect, useRef, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Receipt, TrendingUp, RefreshCw, Megaphone, Building2, BadgeCheck,
  Compass, Map, ShoppingBag, Crosshair, FileText, Layout, BarChart3, FileBarChart,
  Users, LifeBuoy, Bell, Settings, Search, Menu, X, LogOut, ChevronDown, Loader2, Activity, Inbox, Image, MessageSquare, Shirt, Ticket, Package, Truck, Star, Undo2, Sparkles, Film, Handshake,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAdmin } from "@/components/admin/ui";

const NAV = [
  { group: "Overview", items: [{ to: "/admin", label: "Dashboard", icon: LayoutDashboard, perm: "dashboard", end: true }] },
  { group: "Commerce", items: [
    { to: "/admin/sales", label: "Sales", icon: Receipt, perm: "sales_view" },
    { to: "/admin/revenue", label: "Revenue", icon: TrendingUp, perm: "finance" },
    { to: "/admin/subscriptions", label: "Subscriptions", icon: RefreshCw, perm: "subscriptions" },
    { to: "/admin/advertising", label: "Advertising", icon: Megaphone, perm: "advertising" },
  ] },
  { group: "Network", items: [
    { to: "/admin/businesses", label: "Businesses", icon: Building2, perm: "businesses" },
    { to: "/admin/approvals", label: "Approvals", icon: BadgeCheck, perm: "approvals" },
    { to: "/admin/guides", label: "Guides & Outfitters", icon: Compass, perm: "guides" },
    { to: "/admin/partners", label: "Partners", icon: Handshake, perm: "marketplace" },
    { to: "/admin/states", label: "States", icon: Map, perm: "states" },
    { to: "/admin/ai", label: "HomeRange AI", icon: Sparkles, perm: "states" },
    { to: "/admin/data-health", label: "Data Health", icon: Activity, perm: "states" },
    { to: "/admin/review-queue", label: "Review Queue", icon: Inbox, perm: "states" },
  ] },
  { group: "Content", items: [
    { to: "/admin/marketplace", label: "Marketplace", icon: ShoppingBag, perm: "marketplace" },
    { to: "/admin/commerce/products", label: "HomeRange Goods", icon: Shirt, perm: "marketplace" },
    { to: "/admin/commerce/orders", label: "Goods Orders", icon: Package, perm: "marketplace" },
    { to: "/admin/commerce/discounts", label: "Discounts", icon: Ticket, perm: "marketplace" },
    { to: "/admin/commerce/shipping", label: "Shipping Rules", icon: Truck, perm: "marketplace" },
    { to: "/admin/commerce/reviews", label: "Reviews", icon: Star, perm: "marketplace" },
    { to: "/admin/commerce/returns", label: "Returns", icon: Undo2, perm: "marketplace" },
    { to: "/admin/merch", label: "Merch (Printify)", icon: Shirt, perm: "marketplace" },
    { to: "/admin/hunts", label: "Hunts", icon: Crosshair, perm: "hunts" },
    { to: "/admin/content", label: "Content", icon: FileText, perm: "content" },
    { to: "/admin/films", label: "Films & Videos", icon: Film, perm: "content" },
    { to: "/admin/firearms", label: "Firearms Research", icon: Crosshair, perm: "content" },
    { to: "/admin/website", label: "Website", icon: Layout, perm: "website" },
    { to: "/admin/navigation", label: "Navigation", icon: Menu, perm: "website" },
    { to: "/admin/pages", label: "Pages", icon: FileText, perm: "content" },
    { to: "/admin/media", label: "Media Library", icon: Image, perm: "content" },
  ] },
  { group: "Insights", items: [
    { to: "/admin/analytics", label: "Analytics", icon: BarChart3, perm: "analytics" },
    { to: "/admin/reports", label: "Reports", icon: FileBarChart, perm: "reports" },
  ] },
  { group: "Operations", items: [
    { to: "/admin/users", label: "Users", icon: Users, perm: "users" },
    { to: "/admin/archive", label: "Archive Moderation", icon: MessageSquare, perm: "content" },
    { to: "/admin/support", label: "Support", icon: LifeBuoy, perm: "support" },
    { to: "/admin/notifications", label: "Notifications", icon: Bell, perm: "notifications" },
    { to: "/admin/settings", label: "Settings", icon: Settings, perm: "settings" },
  ] },
];

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const timer = useRef();
  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults(null); return; }
    timer.current = setTimeout(async () => {
      setBusy(true);
      try { const d = await api.adminSearch(q.trim()); setResults(d.results); } catch { setResults([]); }
      finally { setBusy(false); }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q]);
  return (
    <div className="relative flex-1 max-w-md">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6B61]" />
      <input
        value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Search orders, users, businesses, hunts, states…"
        className="w-full border border-[#E5E2DC] bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-[#1F2A22]"
        data-testid="admin-global-search"
      />
      {results !== null && q.trim().length >= 2 && (
        <div className="absolute top-full mt-1 w-full bg-white border border-[#E5E2DC] shadow-lg z-50 max-h-80 overflow-y-auto" data-testid="admin-search-results">
          {busy && <div className="p-3 text-sm text-[#5C6B61] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Searching…</div>}
          {!busy && results.length === 0 && <div className="p-3 text-sm text-[#5C6B61]">No matches for “{q}”.</div>}
          {results.map((r) => (
            <button key={`${r.type}-${r.id}`} onClick={() => { navigate(r.link); setQ(""); setResults(null); }}
              className="w-full text-left px-3 py-2 hover:bg-[#F2F0EB] flex items-center justify-between" data-testid="admin-search-result">
              <span className="text-sm text-[#1F2A22]">{r.label}{r.sub ? <span className="text-[#5C6B61]"> · {r.sub}</span> : null}</span>
              <span className="text-[10px] uppercase tracking-wide text-[#8A5A44]">{r.type}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const load = useCallback(() => { api.adminNotifications().then(setData).catch(() => {}); }, []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  const readAll = async () => { await api.adminNotificationsReadAll(); load(); };
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="relative p-2 hover:bg-[#E5E2DC]/50" aria-label="Notifications" data-testid="admin-notifications-bell">
        <Bell size={18} className="text-[#1F2A22]" />
        {data.unread > 0 && <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-[#FF6A00] text-white text-[10px] rounded-full grid place-items-center" data-testid="admin-notif-count">{data.unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-[#E5E2DC] shadow-lg z-50" data-testid="admin-notif-panel">
          <div className="flex items-center justify-between px-4 py-2 border-b border-[#E5E2DC]">
            <span className="text-xs font-serif uppercase tracking-[0.15em] text-[#5C6B61]">Notifications</span>
            {data.unread > 0 && <button onClick={readAll} className="text-xs text-[#FF6A00]" data-testid="admin-notif-readall">Mark all read</button>}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {data.notifications.length === 0 ? (
              <div className="p-4 text-sm text-[#5C6B61]">You're all caught up.</div>
            ) : data.notifications.map((n) => (
              <div key={n.id} className={`px-4 py-3 border-b border-[#E5E2DC] ${n.read ? "opacity-60" : ""}`}>
                <p className="text-sm text-[#1F2A22] font-medium">{n.title}</p>
                {n.body && <p className="text-xs text-[#5C6B61] mt-0.5">{n.body}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminLayout({ children }) {
  const { admin } = useAdmin();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const can = (p) => admin?.permissions?.includes(p);

  const Sidebar = ({ mobile }) => (
    <aside className={`${mobile ? "flex" : "hidden md:flex"} h-full max-h-screen flex-col bg-[#1F2A22] text-[#F9F8F6] overflow-y-auto`}>
      <div className="px-5 py-5 border-b border-white/10">
        <button onClick={() => navigate("/admin")} className="flex items-center gap-2" data-testid="admin-logo">
          <span className="font-serif text-xl tracking-tight">HomeRange</span>
          <span className="text-[10px] uppercase tracking-[0.2em] bg-[#FF6A00] text-white px-1.5 py-0.5">Admin</span>
        </button>
      </div>
      <nav className="flex-1 py-3">
        {NAV.map((section) => {
          const items = section.items.filter((i) => can(i.perm));
          if (!items.length) return null;
          return (
            <div key={section.group} className="mb-2">
              <div className="px-5 py-2 text-[10px] font-serif uppercase tracking-[0.15em] text-white/40">{section.group}</div>
              {items.map((i) => (
                <NavLink key={i.to} to={i.to} end={i.end} onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => `flex items-center gap-3 px-5 py-2 text-sm transition-colors ${isActive ? "bg-white/10 text-white border-l-2 border-[#FF6A00]" : "text-white/70 hover:bg-white/5 hover:text-white border-l-2 border-transparent"}`}
                  data-testid={`admin-nav-${i.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <i.icon size={17} /> {i.label}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>
    </aside>
  );

  return (
    <div className="grid min-h-screen w-full md:grid-cols-[260px_1fr] bg-[#F9F8F6]">
      <Sidebar />
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="admin-mobile-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64"><Sidebar mobile /></div>
        </div>
      )}
      <div className="flex flex-col min-w-0">
        <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-[#E5E2DC] bg-[#F9F8F6]/90 backdrop-blur px-4 md:px-6">
          <button className="md:hidden p-2" onClick={() => setMobileOpen(true)} aria-label="Menu" data-testid="admin-menu-toggle"><Menu size={20} /></button>
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-2">
            <NotificationsBell />
            <div className="relative">
              <button onClick={() => setAcctOpen(!acctOpen)} className="flex items-center gap-2 px-2 py-1.5 hover:bg-[#E5E2DC]/50" data-testid="admin-account-btn">
                <span className="w-8 h-8 bg-[#1F2A22] text-[#F9F8F6] grid place-items-center text-sm font-serif">{(admin?.email || "A")[0].toUpperCase()}</span>
                <ChevronDown size={14} className="text-[#5C6B61] hidden sm:block" />
              </button>
              {acctOpen && (
                <div className="absolute right-0 top-full mt-1 w-60 bg-white border border-[#E5E2DC] shadow-lg z-50" data-testid="admin-account-menu">
                  <div className="px-4 py-3 border-b border-[#E5E2DC]">
                    <p className="text-sm font-medium text-[#1F2A22] truncate">{admin?.name || admin?.email}</p>
                    <p className="text-xs text-[#5C6B61] truncate">{admin?.email}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(admin?.roles || []).map((r) => <span key={r} className="text-[10px] uppercase bg-[#F2F0EB] text-[#5C6B61] px-1.5 py-0.5">{r}</span>)}
                    </div>
                  </div>
                  <button onClick={() => { logout(); navigate("/admin/login"); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#1F2A22] hover:bg-[#F2F0EB]" data-testid="admin-logout"><LogOut size={15} /> Sign out</button>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="flex-1 min-w-0">{children}</main>
      </div>
    </div>
  );
}
